import React from 'react';
import { connect } from 'react-redux';
import { getSearchForm } from 'app/sagas/selectors';
import { compose } from 'redux';
import SearchingLayout from './SearchingLayout';
import posthog from 'posthog-js';
import * as searchResultsActions from 'app/actions/search-results';
import moment from 'moment';
import { ROUND_TRIP_MODE_VALUE } from 'app/constants';
import { DATE_FORMAT, HOUR_FORMAT } from 'app/utils/types';
import { useSearchResultsPageContext } from 'app/pages/SearchResultsPage/SearchResultsPageContext';

let first_result_timestamp = null;
let captureEvent = false;

/**
 * Basic(abstract) container component for showing trips search results.
 * It contains logic for showing error, loading or the results depending
 * on the state of reducers.
 */
const BasicResultsList = ({
  available,
  recommendedTripIds,
  trips,
  searching,
  errored,
  start_address,
  end_address,
  num_passengers,
  is_flight_time,
  pickup_datetime,
  return_pickup_datetime,
  mode,
  booking_mode,
  doSearch,
  components
}) => {
  const { ErrorContainerLayout, ResultsLayout, NoResultsLayout } = components;
  const { is_search_edited, search_button_click_timestamp } = useSearchResultsPageContext();

  if (errored) {
    const props = {
      errorCode: errored.code,
      errorCodeMessage: errored.errorCodeMessage
    };

    if (!errored.skipTryAgain) {
      props.onTryAgain = doSearch
    }

    posthog.capture('Results Page Error', {
      start_address: start_address?.value,
      start_address_type: start_address?.type,
      end_address: end_address?.value,
      end_address_type: end_address?.type,
      pickup_date: moment(pickup_datetime).format(DATE_FORMAT),
      pickup_time: moment(pickup_datetime).format(HOUR_FORMAT),
      is_landing_time_selected: is_flight_time,
      passenger_count: num_passengers,
      is_return_added: mode === ROUND_TRIP_MODE_VALUE ? true : false,
      return_pickup_date: moment(return_pickup_datetime).format(DATE_FORMAT),
      return_pickup_time: moment(return_pickup_datetime).format(HOUR_FORMAT),
      is_return_landing_time_selected: mode === ROUND_TRIP_MODE_VALUE ? true : false,
      results_error_code: errored.errorCodeMessage,
      results_error_message: errored.message
    });

    return <ErrorContainerLayout {...props} />;
  } else if (available.length > 0 || recommendedTripIds?.length > 0) {
    if (!first_result_timestamp && searching) {
      first_result_timestamp = performance.now();
    }

    if(searching){
      captureEvent = true;
    }

    if (!searching && captureEvent) {
      posthog.capture('Results Page Viewed', {
        results_count: available.length,
        first_result_duration_ms: Math.abs(Math.round(first_result_timestamp - search_button_click_timestamp)),
        is_search_edited: is_search_edited,
      });

      first_result_timestamp = null;
      captureEvent = false;
    }

    return (
      <ResultsLayout
        trips={trips}
        available={available}
        searching={searching}
      />
    );
  } else if (searching) {
    return (
      <SearchingLayout
        mode={mode}
        bookingMode={booking_mode}
        startAddress={start_address}
        endAddress={end_address}
      />
    );
  }

  posthog.capture('No Results', {
    start_address: start_address?.value,
    start_address_type: start_address?.type,
    start_address_id: start_address?.place_id,
    end_address: end_address?.value,
    end_address_type: end_address?.type,
    end_address_id: end_address?.place_id,
    passenger_count: num_passengers,
  });

  return <NoResultsLayout />;
};

// TODO: move to Flow types
// BasicResultsList.propTypes = {
//   available: PropTypes.array,
//   recommendedTripId: PropTypes.string,
//   end_address: PropTypes.object,
//   start_address: PropTypes.object,
//   trips: PropTypes.object,
//   mode: PropTypes.string,
//   booking_mode: PropTypes.string,
//   doSearch: PropTypes.func,
//   errored: PropTypes.shape({
//     code: PropTypes.number
//   }),
//   searching: PropTypes.bool,
//   components: PropTypes.object.isRequired
// };

const mapStateToProps = (state) => {
  const {
    searchResults: { trips, searching, errored, available, recommendedTripIds },
    userSession: { user }
  } = state;

  const {
    start_address,
    end_address,
    mode,
    booking_mode,
    num_passengers,
    is_flight_time,
    pickup_datetime,
    return_pickup_datetime
  } = getSearchForm(state);

  return {
    start_address,
    recommendedTripIds,
    end_address,
    mode,
    booking_mode,
    num_passengers,
    is_flight_time,
    pickup_datetime,
    return_pickup_datetime,
    available,
    user,
    trips,
    searching,
    errored
  };
};

const mapDispatchToProps = { doSearch: searchResultsActions.doSearch };

// Bring it all together
export default compose(connect(mapStateToProps, mapDispatchToProps))(
  BasicResultsList
);
